import { AbstractControl, ValidationErrors } from '@angular/forms';
import { DATE_FORMAT } from "@app/app.constant";
import { isDate } from "@app/app.helper";
import { InputControllerTypesToValueType, ComponetValueType } from "@app/models/common/common.model";
import { // tslint:disable-next-line: no-bitwise
  InputControllerTypes
} from "common/enums";
import { IDataStringExtraValue } from "common/model";
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { getWithExtraValue } from 'projects/evide-client/src/app/common/form.helper';

export const uuid = (): string => {
  let dt = new Date().getTime();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    // tslint:disable-next-line: no-bitwise
    const r = (dt + Math.random() * 16) % 16 | 0;
    dt = Math.floor(dt / 16);
    // tslint:disable-next-line: no-bitwise
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
};

const unknownError = 'Something went wrong. Please try again.';

// Allocation-cap rejections are advisory limits the staff member can lift by extending the
// allocation — not application errors. Route them to a warning (amber) toast so the visual
// weight matches the action. Styled in theme/scss/custom/plugins/_toastr.scss.
const isAllocationCapMessage = (msg: string): boolean =>
  /extend the session allocation/i.test(msg);

export const onErrorAlert = (alert: ToastrService, err?: any, config?: any) => {
  if (err?.status === 0) {
    return;
  }
  // 403 = backend rolemapping denied the action. Bare ForbidResult has no body, which
  // otherwise falls through to a generic "Something went wrong" toast — confusing when
  // the real cause is "your role doesn't have permission". Surface nothing instead;
  // the UI should already be hiding entry points for actions the user can't perform.
  if (err?.status === 403) {
    return;
  }
  const emit = (msg: string) => {
    if (isAllocationCapMessage(msg)) {
      alert.warning(msg, undefined, config);
    } else {
      alert.error(msg, undefined, config);
    }
  };
  if (typeof err === 'string' || err instanceof String) {
    emit(err.toString());
  } else if (typeof err?.error === 'string' || err?.error instanceof String) {
    emit(err.error.toString());
  } else if (typeof err?.errorMessage === 'string' || err?.errorMessage instanceof String) {
    emit(err.errorMessage.toString());
  } else if (typeof err?.error?.errorMessage === 'string' || err?.error?.errorMessage instanceof String) {
    emit(err.error.errorMessage.toString());
  } else if (typeof err?.error?.message === 'string' || err?.error?.message instanceof String) {
    emit(err.error.message.toString());
  } else if (typeof err?.error?.detail === 'string' || err?.error?.detail instanceof String) {
    emit(err.error.detail.toString());
  } else if (err?.error?.title) {
    emit(err.error.title);
  } else {
    alert.error(unknownError, undefined, config);
  }
};

/**
 * Show a specific error message — unless the underlying error was a 403 (permission
 * denied) or status 0 (network down), in which case stay silent. Use this in place of
 * direct `toastr.error('Failed to ...')` calls inside HttpClient `error:` handlers
 * so a missing rolemapping doesn't produce a misleading "Failed to ..." toast.
 */
export const notifyError = (alert: ToastrService, err: any, message: string) => {
  if (err?.status === 0 || err?.status === 403) return;
  alert.error(message);
};

export const onInformationAlert = (alert: ToastrService, title?: string, message?: string) => {
  if (message !== undefined) {
    alert.info(message);
  } else {
    alert.info(unknownError);
  }
};

// Mandatory check for a dropdown-with-comment (8192). Its control holds
// { dataStringValue, dataStringExtraValue } once the user has interacted, and Validators.required
// treats any object as filled - so picking an option and going back to "Select Option", or typing
// a comment without choosing, let a mandatory field through empty. The option is what is
// mandatory; a comment alone is not an answer. Reports under the same 'required' key the
// asterisk directive and error messages already read.
export const optionWithCommentRequired = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;
  const option = value && typeof value === 'object' ? value.dataStringValue : value;
  return option === null || option === undefined || String(option).trim() === '' ? { required: true } : null;
};

// Used while saving the record
export const inputControllerTypesToValue =(inputControllerType: InputControllerTypes | undefined, value: any) => {
  let dataStringValue: string | null = null;
  let dataStringValues: string[] | null = null;
  let dataStringExtraValues: Array<IDataStringExtraValue> | null = null;
  let dataStringExtraValue = null;

  if (inputControllerType === InputControllerTypes.Checkboxes || inputControllerType === InputControllerTypes.Checkbox) {
    const valArr = Array.isArray(value) ? value : [value];
    valArr.forEach(item => {
      const val: [string | undefined, IDataStringExtraValue | undefined] = getWithExtraValue(item);
      if (val[1]) {
        if (!dataStringExtraValues) { dataStringExtraValues = []; }
        dataStringExtraValues.push(val[1] as IDataStringExtraValue);
      } else if (val[0] !== undefined) {
        if (!dataStringValues) { dataStringValues = []; }
        dataStringValues.push(val[0]);
      }
    });
  } else if (inputControllerType === InputControllerTypes.DropdownWithExtraText || inputControllerType === InputControllerTypes.RadioButtons) {
    dataStringValue = value.dataStringValue;
    dataStringExtraValue = value.dataStringExtraValue;
  } else if (inputControllerType === InputControllerTypes.DropdownMulti) {
    dataStringValues = Array.isArray(value) ? value : [value];
  } else if (inputControllerType === InputControllerTypes.DateTimePicker) {
    dataStringValue = value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : null;
  } else if (inputControllerType === InputControllerTypes.DatePicker) {
    dataStringValue = value ? moment(value).format('YYYY-MM-DD') : null;
  } else if (inputControllerType === InputControllerTypes.TimePicker) {
    dataStringValue = value ? moment(value).format('HH:mm:ss') : null;
  } else {
    dataStringValue = Array.isArray(value) ? value.join(',') : value;
  }

  dataStringValue = dataStringValue ? String(dataStringValue) : dataStringValue;
  dataStringExtraValue = dataStringExtraValue ? String(dataStringExtraValue) : dataStringExtraValue;

  return {
    dataStringExtraValue,
    dataStringExtraValues,
    dataStringValue,
    dataStringValues
  };
};

// Used while Getting records
export const valueToControllerType = (controllerType: InputControllerTypes, value: any) => {
  if (InputControllerTypesToValueType[controllerType] === ComponetValueType.Array) {
    if (!Array.isArray(value)) {
      value = value ? [value] : [];
    }
  } else if (InputControllerTypesToValueType[controllerType] === ComponetValueType.DateTime) {
    const dateValue = value;
    value = value ? moment(value, 'YYYY-MM-DD', true).toDate() : undefined;
    value = isNaN(Date.parse(value)) ? moment(dateValue, 'YYYY/MM/DD', true).toDate() : value;
    value = isNaN(Date.parse(value)) ? moment(dateValue, 'DD/MM/YYYY', true).toDate() : value;
    // DateTimePicker also routes here but is SAVED as 'YYYY-MM-DD HH:mm:ss' (see
    // inputControllerTypesToValue) — parse the datetime formats too, else the time-bearing value
    // matches none of the date-only formats above and re-hydrates blank (silent loss on reload).
    value = isNaN(Date.parse(value)) ? moment(dateValue, 'YYYY-MM-DD HH:mm:ss', true).toDate() : value;
    value = isNaN(Date.parse(value)) ? moment(dateValue, 'YYYY-MM-DDTHH:mm:ss', true).toDate() : value;
    value = isNaN(Date.parse(value)) ? undefined : value;
  } else if (InputControllerTypesToValueType[controllerType] === ComponetValueType.Time) {
    value = value ? moment(value, 'hh:mm:ss').toDate() : value;
  } else if (controllerType === InputControllerTypes.Dropdown) {
    value = value ? value : '';
  } else if (controllerType === InputControllerTypes.Toggle) {
    value = value === "false" || value === false ? false : value === "true" || value === true ? true : value;
  }
  else {
    if (Array.isArray(value)) {
      value = value ? value.join(',') : '';
    }
  }
  return value;
}


/**
 * 
 * @param input: string | IGridColumnData
 * @returns boolean
 */
export const isStringDate = (input: any) => {
  if (typeof (input) === 'string') {
    return isDate(input);
  } else {
    return false;
  }
}

/**
* 
* @param input: string | IGridColumnData
* @returns string
*/
export const setDateFormat = (input: any) => {
  if (typeof (input) === 'string') {
    return moment(input).format(DATE_FORMAT);
  }
}

/**
 * 
 * @param input: string | IGridColumnData
 * @returns boolean
 */
export const isString = (input: any): input is string => {
  if (typeof (input) === 'string') {
    return true;
  } else {
    return false;
  }
}


export const isArray = <T>(input: any): input is Array<T> => {
  return Array.isArray(input)
}
/**
 * Groups a dropdown option list into <optgroup> buckets by a per-option heading.
 * ADJACENT options sharing the same heading form one group (so the author's pill
 * order controls both option order and group order); options without a heading
 * render bare. Returns [{ heading: null | string, options: [...] }] in input order.
 */
export const groupOptionsByHeading = <T>(
  options: ReadonlyArray<T> | null | undefined,
  getHeading: (option: T) => string | null | undefined
): { heading: string | null; options: T[] }[] => {
  const groups: { heading: string | null; options: T[] }[] = [];
  for (const option of options ?? []) {
    const heading = (getHeading(option) || '').trim() || null;
    const last = groups[groups.length - 1];
    if (last && last.heading === heading) {
      last.options.push(option);
    } else {
      groups.push({ heading, options: [option] });
    }
  }
  return groups;
}

// Memoized grouping for templates that render `optionsObs | async`. Select components must
// group whatever the LIVE options source emits - the answer/detail-info base components swap
// optionsObs when a rule execution returns, so a one-shot ngOnInit grouping renders
// rule-populated dropdowns (allocated staff etc.) permanently empty. Memoized by array
// identity so ngFor keeps stable group instances across change-detection cycles.
export const createOptionGrouper = <T>(
  getHeading: (option: T) => string | null | undefined
): ((options: ReadonlyArray<T> | null | undefined) => { heading: string | null; options: T[] }[]) => {
  let lastOptions: ReadonlyArray<T> | null | undefined;
  let lastGroups: { heading: string | null; options: T[] }[] = [];
  return (options) => {
    if (options !== lastOptions) {
      lastOptions = options;
      lastGroups = groupOptionsByHeading(options, getHeading);
    }
    return lastGroups;
  };
}
